import {
  DataPointValue,
  DATE_FILTER_COMPARISONS,
  DATE_RANGE_COMPARISONS,
  DateFilterType,
  DateRangeFilter,
  DYNAMIC_DATE_COMPARISONS,
  ICustomReportFilter,
  isDateRangeFilter,
  MeasureDataType,
  MeasureFormatter,
  NUMBER_FILTER_COMPARISONS,
  REPORT_FILTER_COMPARISON_LABELS,
  ReportFilterComparison,
  SELECT_FILTER_COMPARISONS,
  TEXT_FILTER_COMPARISONS,
  TimingDirection,
} from '@principle-theorem/principle-core/interfaces';
import {
  IBaseMeasures,
  ICustomReportFilterFormatter,
  ICustomReportFilterSummary,
  isCanGroupMeasuresProperty,
  ReportingProperty,
} from '@principle-theorem/reporting/interfaces';
import {
  AtLeast,
  DAY_MONTH_YEAR_FORMAT,
  ISelectOption,
  isISODateType,
  ITimePeriod,
  toTimePeriod,
} from '@principle-theorem/shared';
import { compact, first, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { ReportBuilderDataSource } from '../data-sources/report-builder-data-source';
import { resolveReportingProperty } from './fact-table-resolvers';

export class CustomReportFilter {
  static init(
    overrides: AtLeast<ICustomReportFilter, 'id' | 'values'>
  ): ICustomReportFilter {
    return {
      uid: uuid(),
      ...overrides,
    };
  }

  static initDateRange(
    overrides: AtLeast<ICustomReportFilter, 'id'>
  ): ICustomReportFilter {
    return CustomReportFilter.init({
      comparison: ReportFilterComparison.DateIsBetween,
      dateType: DateFilterType.ReportDate,
      values: [],
      ...overrides,
    });
  }

  static getProperty(
    factTable: IBaseMeasures,
    filter: ICustomReportFilter
  ): ReportingProperty | undefined {
    return resolveReportingProperty(factTable, filter.id);
  }

  static getFormatter(
    factTable: IBaseMeasures,
    filter: ICustomReportFilter
  ): ICustomReportFilterFormatter {
    const metadata = CustomReportFilter.getProperty(factTable, filter)
      ?.metadata;
    const formatterValue = isString(metadata?.formatterValue)
      ? metadata?.formatterValue
      : undefined;
    return {
      formatter: metadata?.formatter,
      prefix:
        metadata?.formatter === MeasureFormatter.Prefix
          ? formatterValue
          : undefined,
      suffix:
        metadata?.formatter === MeasureFormatter.Suffix
          ? formatterValue
          : undefined,
    };
  }

  static getComparison(filter: ICustomReportFilter): ReportFilterComparison {
    if (filter.comparison) {
      return filter.comparison;
    }
    const firstValue = first(filter.values);
    if (!firstValue) {
      return ReportFilterComparison.IsEqualTo; // IsEmpty
    }
    if (isDateRangeFilter(firstValue.value)) {
      return ReportFilterComparison.DateIsBetween;
    }
    if (isISODateType(firstValue.value)) {
      return ReportFilterComparison.DateIs;
    }
    if (isString(firstValue.value)) {
      return ReportFilterComparison.TextIsExactly;
    }
    return ReportFilterComparison.IsEqualTo;
  }

  static getValidComparisonsForProperty(
    property: ReportingProperty
  ): ReportFilterComparison[] {
    if (property.metadata.dataType === MeasureDataType.Boolean) {
      return [ReportFilterComparison.IsEqualTo];
    }
    if (property.metadata.optionResolver) {
      return SELECT_FILTER_COMPARISONS;
    }
    if (property.metadata.dataType === MeasureDataType.String) {
      return TEXT_FILTER_COMPARISONS;
    }
    if (property.metadata.dataType === MeasureDataType.Number) {
      return NUMBER_FILTER_COMPARISONS;
    }
    if (property.metadata.dataType === MeasureDataType.Date) {
      return DATE_FILTER_COMPARISONS;
    }
    return [ReportFilterComparison.IsEqualTo];
  }

  static getValidFilterComparison(
    property: ReportingProperty,
    filter: ICustomReportFilter
  ): ReportFilterComparison {
    const validComparisons =
      CustomReportFilter.getValidComparisonsForProperty(property);
    const comparison = CustomReportFilter.getComparison(filter);
    if (validComparisons.includes(comparison)) {
      return comparison;
    }
    return first(validComparisons) ?? ReportFilterComparison.IsEqualTo;
  }

  static getLabel(property: ReportingProperty): string {
    return ReportBuilderDataSource.getPropertyLabel(property);
  }

  static getComparisonDisplay(
    comparison: ReportFilterComparison,
    isMultiSelect: boolean = false
  ): string {
    if (isMultiSelect) {
      return comparison === ReportFilterComparison.IsNotEqualTo
        ? 'is not any of'
        : 'is any of';
    }
    return REPORT_FILTER_COMPARISON_LABELS[comparison];
  }

  static getSummarySegments(
    factTable: IBaseMeasures,
    filter: ICustomReportFilter
  ): ICustomReportFilterSummary | undefined {
    const property = CustomReportFilter.getProperty(factTable, filter);
    if (!property) {
      return;
    }

    const label = CustomReportFilter.getLabel(property);
    const comparison = CustomReportFilter.getComparison(filter);
    const isMultiSelect =
      property.metadata.dataType !== MeasureDataType.Boolean &&
      !!property.metadata.optionResolver;
    const comparisonDisplay = CustomReportFilter.getComparisonDisplay(
      comparison,
      isMultiSelect
    );

    const value = CustomReportFilter.getValueDisplay(filter, comparison);
    const isArrayProperty =
      isCanGroupMeasuresProperty(property) &&
      !!property.groupMeasure.arrayDataAccessor;
    return {
      label,
      isArray: isArrayProperty,
      comparison: comparisonDisplay,
      value,
    };
  }

  static getSummary(
    factTable: IBaseMeasures,
    filter: ICustomReportFilter
  ): string {
    const segments = CustomReportFilter.getSummarySegments(factTable, filter);
    if (!segments) {
      return 'Unknown Filter';
    }
    if (segments.isArray) {
      return `Has ${segments.label} = ${segments.value}`;
    }
    return `${segments.label} ${segments.comparison} ${segments.value}`;
  }

  static getValueDisplay(
    filter: ICustomReportFilter,
    comparison: ReportFilterComparison
  ): string {
    if (DATE_FILTER_COMPARISONS.includes(comparison)) {
      if (DYNAMIC_DATE_COMPARISONS.includes(comparison)) {
        if (!filter.relativeOffset) {
          return `Invalid Value`;
        }
        const { amount, unit, direction } = filter.relativeOffset;
        return `${amount} ${unit} ${direction}`;
      }

      if (filter.dateType === DateFilterType.ReportDate) {
        return DATE_RANGE_COMPARISONS.includes(comparison)
          ? `Selected Date Range`
          : `Selected Date`;
      }
    }

    if (!filter.values.length) {
      return `Invalid Value`;
    }
    return filter.values.map((filterValue) => filterValue.label).join(' or ');
  }

  static getComparisonOptions(
    values: ReportFilterComparison[]
  ): ISelectOption<ReportFilterComparison>[] {
    return values.map((value) => CustomReportFilter.getComparisonOption(value));
  }

  static getComparisonOption(
    value: ReportFilterComparison
  ): ISelectOption<ReportFilterComparison> {
    return {
      label: REPORT_FILTER_COMPARISON_LABELS[value],
      value,
    };
  }

  static applyDatesToFilters(
    filters: ICustomReportFilter[],
    filterDate: moment.Moment = moment(),
    filterDateRange: ITimePeriod = toTimePeriod(moment(), moment())
  ): ICustomReportFilter[] {
    return filters.map((filter) =>
      CustomReportFilter.applyDates(filter, filterDate, filterDateRange)
    );
  }

  static applyDates(
    filter: ICustomReportFilter,
    filterDate: moment.Moment = moment(),
    filterDateRange: ITimePeriod = toTimePeriod(moment(), moment())
  ): ICustomReportFilter {
    const date = filterDate.startOf('day');
    const dateRange = toTimePeriod(
      filterDateRange.from.startOf('day'),
      filterDateRange.to.endOf('day')
    );
    if (this.isDynamicDateFilter(filter)) {
      const dynamicDate = this.translateRelativeDate(filter, date);
      return this.filterWithValue(filter, dynamicDate);
    }

    if (this.isDateFilter(filter)) {
      return this.filterWithValue(filter, date);
    }

    if (this.isDateRangeFilter(filter)) {
      return this.filterWithValue(filter, dateRange.from, dateRange.to);
    }

    return filter;
  }

  static isDateFilter(filter: ICustomReportFilter): boolean {
    if (!this.isReportDateFilter(filter)) {
      return false;
    }
    if (!filter.comparison) {
      return false;
    }
    return !DATE_RANGE_COMPARISONS.includes(filter.comparison);
  }

  static isDateRangeFilter(filter: ICustomReportFilter): boolean {
    if (!this.isReportDateFilter(filter)) {
      return false;
    }
    return (
      !filter.comparison || DATE_RANGE_COMPARISONS.includes(filter.comparison)
    );
  }

  static isReportDateFilter(filter: ICustomReportFilter): boolean {
    const comparison = CustomReportFilter.getComparison(filter);
    if (!DATE_FILTER_COMPARISONS.includes(comparison)) {
      return false;
    }
    if (filter.dateType !== DateFilterType.SpecificDate) {
      return true;
    }
    return DYNAMIC_DATE_COMPARISONS.includes(comparison);
  }

  static isDynamicDateFilter(filter: ICustomReportFilter): boolean {
    const comparison = CustomReportFilter.getComparison(filter);
    if (!DATE_FILTER_COMPARISONS.includes(comparison)) {
      return false;
    }
    return DYNAMIC_DATE_COMPARISONS.includes(comparison);
  }

  static translateRelativeDate(
    filter: ICustomReportFilter,
    relativeDate: moment.Moment = moment()
  ): moment.Moment {
    if (
      !filter.comparison ||
      !filter.relativeOffset ||
      !DYNAMIC_DATE_COMPARISONS.includes(filter.comparison)
    ) {
      return relativeDate;
    }
    const { direction, amount, unit } = filter.relativeOffset;
    switch (direction) {
      case TimingDirection.Before:
        return moment(relativeDate).subtract(amount, unit);
      case TimingDirection.After:
        return moment(relativeDate).add(amount, unit);
      default:
        return relativeDate;
    }
  }

  static filterWithValue(
    filter: ICustomReportFilter,
    from: moment.Moment,
    to?: moment.Moment
  ): ICustomReportFilter {
    return {
      ...filter,
      values: [
        {
          label: this.getDateLabel(from, to),
          value: CustomReportFilter.getDateFilterValue(filter, from, to),
        },
      ],
    };
  }

  static getDateLabel(from: moment.Moment, to?: moment.Moment): string {
    return compact([
      from.format(DAY_MONTH_YEAR_FORMAT),
      to?.format(DAY_MONTH_YEAR_FORMAT),
    ]).join(' - ');
  }

  static getDateFilterValue(
    filter: ICustomReportFilter,
    from: moment.Moment,
    to?: moment.Moment
  ): DataPointValue | DateRangeFilter {
    const comparison = CustomReportFilter.getComparison(filter);

    // Date range comparisons should return a date range inclusive of the dates given
    // If a single date is given it should be treated as a range start - end of day
    if (
      comparison === ReportFilterComparison.DateIs ||
      DATE_RANGE_COMPARISONS.includes(comparison)
    ) {
      const start = from;
      const end = to ? to : from;
      return [
        start.startOf('day').toISOString(),
        end.endOf('day').toISOString(),
      ];
    }

    // Before & after comparisons do not include the day in question
    if (comparison === ReportFilterComparison.DateIsBefore) {
      return from.startOf('day').toISOString();
    }
    if (comparison === ReportFilterComparison.DateIsAfter) {
      return from.endOf('day').toISOString();
    }

    // At least should include the day in question
    if (comparison === ReportFilterComparison.DateIsAtLeast) {
      return filter.relativeOffset?.direction === TimingDirection.After
        ? from.startOf('day').toISOString()
        : from.endOf('day').toISOString();
    }

    // At most should include the day in question
    if (comparison === ReportFilterComparison.DateIsAtMost) {
      return filter.relativeOffset?.direction === TimingDirection.After
        ? from.endOf('day').toISOString()
        : from.startOf('day').toISOString();
    }

    return from.startOf('day').toISOString();
  }
}
